import { useState } from 'react';
import { authAPI } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { InputOTP, InputOTPGroup, InputOTPSlot } from '@/components/ui/input-otp';
import { toast } from 'sonner';
import { ShieldCheck, ShieldOff, QrCode } from 'lucide-react';

export default function TwoFADialog({ open, onClose }) {
  const { user, checkAuth } = useAuth();
  const [step, setStep] = useState('idle'); // idle, setup, verify
  const [qrCode, setQrCode] = useState('');
  const [secret, setSecret] = useState('');
  const [otpCode, setOtpCode] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSetup = async () => {
    setLoading(true);
    try {
      const res = await authAPI.setup2FA();
      setQrCode(res.data.qr_code);
      setSecret(res.data.secret);
      setStep('setup');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur');
    }
    setLoading(false);
  };

  const handleEnable = async () => {
    if (otpCode.length !== 6) return;
    setLoading(true);
    try {
      await authAPI.enable2FA({ secret, totp_code: otpCode });
      toast.success('2FA activé avec succès');
      await checkAuth();
      setStep('idle');
      setOtpCode('');
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Code invalide');
      setOtpCode('');
    }
    setLoading(false);
  };

  const handleDisable = async () => {
    if (!window.confirm('Désactiver la double authentification ?')) return;
    setLoading(true);
    try {
      await authAPI.disable2FA();
      toast.success('2FA désactivé');
      await checkAuth();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur');
    }
    setLoading(false);
  };

  const handleClose = () => {
    setStep('idle');
    setOtpCode('');
    setQrCode('');
    setSecret('');
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent data-testid="2fa-dialog" className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ShieldCheck className="w-5 h-5" strokeWidth={1.5} />
            Double authentification (2FA)
          </DialogTitle>
          <DialogDescription>
            {user?.two_fa_enabled ? '2FA est actuellement activé' : 'Protégez votre compte avec Google Authenticator'}
          </DialogDescription>
        </DialogHeader>

        {step === 'idle' && (
          <div className="space-y-4 py-2">
            {user?.two_fa_enabled ? (
              <div className="space-y-3">
                <div className="flex items-center gap-2 bg-emerald-50 text-emerald-700 rounded-lg p-3 text-sm">
                  <ShieldCheck className="w-4 h-4" strokeWidth={1.5} />
                  <span>La double authentification est activée</span>
                </div>
                <Button
                  data-testid="disable-2fa-btn"
                  variant="destructive"
                  className="w-full"
                  onClick={handleDisable}
                  disabled={loading}
                >
                  <ShieldOff className="w-4 h-4 mr-2" strokeWidth={1.5} />
                  Désactiver 2FA
                </Button>
              </div>
            ) : (
              <Button
                data-testid="setup-2fa-btn"
                className="w-full"
                onClick={handleSetup}
                disabled={loading}
              >
                <QrCode className="w-4 h-4 mr-2" strokeWidth={1.5} />
                {loading ? 'Chargement...' : 'Configurer 2FA'}
              </Button>
            )}
          </div>
        )}

        {step === 'setup' && (
          <div className="space-y-4 py-2">
            <div className="text-center">
              <p className="text-sm text-slate-600 mb-3">
                Scannez ce QR code avec Google Authenticator
              </p>
              {qrCode && (
                <img
                  data-testid="2fa-qr-code"
                  src={qrCode}
                  alt="QR Code 2FA"
                  className="mx-auto rounded-lg border border-slate-200 shadow-sm"
                />
              )}
              <p className="text-xs text-slate-400 mt-3 font-data break-all">
                Clé manuelle: {secret}
              </p>
            </div>

            <div className="space-y-2">
              <Label>Entrez le code de vérification</Label>
              <div className="flex justify-center">
                <InputOTP
                  data-testid="2fa-setup-otp"
                  maxLength={6}
                  value={otpCode}
                  onChange={setOtpCode}
                >
                  <InputOTPGroup>
                    <InputOTPSlot index={0} />
                    <InputOTPSlot index={1} />
                    <InputOTPSlot index={2} />
                    <InputOTPSlot index={3} />
                    <InputOTPSlot index={4} />
                    <InputOTPSlot index={5} />
                  </InputOTPGroup>
                </InputOTP>
              </div>
            </div>

            <Button
              data-testid="enable-2fa-btn"
              className="w-full"
              onClick={handleEnable}
              disabled={loading || otpCode.length !== 6}
            >
              {loading ? 'Vérification...' : 'Activer 2FA'}
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
